import { createAsyncThunk } from '@reduxjs/toolkit';
import { CreateUsersReqData, UpdateUserReqData, userService } from 'services/userService';

const basePath = 'users/';

enum UsersPaths {
  GET_USERS = 'getUsers',
  CREATE_USERS = 'createUsers',
  UPDATE_USER = 'updateUser',
  RESEND_INVITE = 'resendInvite',
  DELETE_USER = 'deleteUser',
  GET_CREDENTIALS = 'getCredentials',
  UPDATE_CREDENTIALS = 'updateCredentials',
  CHECK_USER_EMAIL = 'checkUserEmail',
}

type UpdateUserActionType = { userData: UpdateUserReqData; userID: string };

const getActionName = (path: UsersPaths) => basePath + path;

const getUsers = createAsyncThunk(getActionName(UsersPaths.GET_USERS), async () => {
  return await userService.getUsers();
});

const createUser = createAsyncThunk(
  getActionName(UsersPaths.CREATE_USERS),
  async (userData: CreateUsersReqData, thunkAPI) => {
    try {
      await userService.createUser(userData);
      return thunkAPI.fulfillWithValue({ isCreated: true });
    } catch (err: any) {
      return thunkAPI.rejectWithValue(err?.response?.data);
    }
  },
);

const resendInvite = createAsyncThunk(getActionName(UsersPaths.RESEND_INVITE), async (userId: string) => {
  return await userService.resendInvite(userId);
});

const updateUser = createAsyncThunk(
  getActionName(UsersPaths.UPDATE_USER),
  async ({ userData, userID }: UpdateUserActionType, thunkAPI) => {
    try {
      await userService.updateUser(userData, userID);
      return thunkAPI.fulfillWithValue({ isUpdated: true });
    } catch (err: any) {
      return thunkAPI.rejectWithValue(err?.response?.data);
    }
  },
);

const deleteUser = createAsyncThunk(getActionName(UsersPaths.DELETE_USER), async (userID: string) => {
  return await userService.deleteUser(userID);
});

const getCredentials = createAsyncThunk(getActionName(UsersPaths.GET_CREDENTIALS), async (userID: string) => {
  return await userService.getCredentials(userID);
});

const updateCredentials = createAsyncThunk(getActionName(UsersPaths.GET_CREDENTIALS), async (userID: string) => {
  return await userService.updateCredentials(userID);
});

const checkUserEmail = createAsyncThunk(getActionName(UsersPaths.CHECK_USER_EMAIL), async (email: string, thunkAPI) => {
  try {
    return await userService.checkEmail(email);
  } catch (err) {
    return thunkAPI.rejectWithValue(err);
  }
});

export const asyncUsersActions = {
  getUsers,
  createUser,
  updateUser,
  resendInvite,
  deleteUser,
  getCredentials,
  updateCredentials,
  checkUserEmail,
};
